import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { LibrarySection, CreateLibrarySectionRequest, UpdateLibrarySectionRequest } from '@/types/api';
import { LibrarySectionSource } from '@/types/enums';
import { formatMessageRange } from '@/utils/formatters';
import { useManuscriptChats } from '@/hooks/useMessages';
import { useChatLabel } from '@/hooks/useChatLabel';
import { formatDate } from '@/utils/dateUtils';

interface SectionEditorProps {
  open: boolean;
  libraryName: string;
  sections: LibrarySection[];
  onClose: () => void;
  onAddSection: (data: CreateLibrarySectionRequest) => void;
  onUpdateSection: (sectionId: number, data: UpdateLibrarySectionRequest) => void;
  onDeleteSection: (sectionId: number) => void;
  isLoading?: boolean;
}

const SectionEditor: React.FC<SectionEditorProps> = ({
  open,
  libraryName,
  sections,
  onClose,
  onAddSection,
  onUpdateSection,
  onDeleteSection,
  isLoading = false,
}) => {
  const { t } = useTranslation('components');
  const chatLabel = useChatLabel();
  const [chatId, setChatId] = useState<number | null>(null);
  const [startMessageId, setStartMessageId] = useState('');
  const [endMessageId, setEndMessageId] = useState('');
  const [source, setSource] = useState<LibrarySectionSource>(LibrarySectionSource.MANUAL);
  const [notes, setNotes] = useState('');
  const { data: chats = [], isLoading: chatsLoading } = useManuscriptChats();
  const [editingSection, setEditingSection] = useState<LibrarySection | null>(null);

  useEffect(() => {
    if (chatId === null && chats.length > 0) {
      setChatId(chats[0].id);
    }
  }, [chatId, chats]);

  const isBusy = isLoading || chatsLoading;

  const handleAddSection = () => {
    if (!chatId) {
      alert(t('sectionEditor.addSection.alertNoChat'));
      return;
    }

    const start = parseInt(startMessageId, 10);
    const end = parseInt(endMessageId, 10);

    if (Number.isNaN(start) || Number.isNaN(end)) {
      alert(t('sectionEditor.addSection.alertInvalidMessageIds'));
      return;
    }

    if (start >= end) {
      alert(t('sectionEditor.addSection.alertStartAfterEnd'));
      return;
    }

    onAddSection({
      chat_id: chatId,
      start_message_id: start,
      end_message_id: end,
      source: source,
      notes: notes.trim() || undefined,
    });

    // Reset form
    setStartMessageId('');
    setEndMessageId('');
    setSource(LibrarySectionSource.MANUAL);
    setNotes('');
  };

  const handleUpdateSection = (sectionId: number, data: UpdateLibrarySectionRequest) => {
    onUpdateSection(sectionId, data);
    setEditingSection(null);
  };

  const getSourceColor = (src: LibrarySectionSource) => {
    switch (src) {
      case LibrarySectionSource.MANUAL:
        return 'primary';
      case LibrarySectionSource.FIHRIS_ASSISTED:
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" component="div">
          {t('sectionEditor.title')}
        </Typography>
        <Typography
          variant="body2"
          component="div"
          color="text.secondary"
          sx={{ direction: 'rtl', textAlign: 'right' }}
        >
          {libraryName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Add Section Form */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('sectionEditor.addSection.title')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small" disabled={isBusy || chats.length === 0}>
                <InputLabel>{t('sectionEditor.addSection.chatLabel')}</InputLabel>
                <Select
                  value={chatId ?? ''}
                  onChange={(e) => setChatId(Number(e.target.value))}
                  disabled={isBusy}
                  label={t('sectionEditor.addSection.chatLabel')}
                  displayEmpty
                >
                  {chats.map((chat) => (
                    <MenuItem key={chat.id} value={chat.id}>
                      {chatLabel(chat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={t('sectionEditor.addSection.startMessageIdLabel')}
                type="number"
                size="small"
                fullWidth
                value={startMessageId}
                onChange={(e) => setStartMessageId(e.target.value)}
                disabled={isBusy}
                placeholder="e.g., 100"
              />

              <TextField
                label={t('sectionEditor.addSection.endMessageIdLabel')}
                type="number"
                size="small"
                fullWidth
                value={endMessageId}
                onChange={(e) => setEndMessageId(e.target.value)}
                disabled={isBusy}
                placeholder="e.g., 500"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('sectionEditor.addSection.sourceLabel')}</InputLabel>
                <Select
                  value={source}
                  onChange={(e) => setSource(e.target.value as LibrarySectionSource)}
                  disabled={isLoading}
                  label={t('sectionEditor.addSection.sourceLabel')}
                >
                  <MenuItem value={LibrarySectionSource.MANUAL}>{t('sectionEditor.addSection.manualSource')}</MenuItem>
                  <MenuItem value={LibrarySectionSource.FIHRIS_ASSISTED}>{t('sectionEditor.addSection.fihrisAssistedSource')}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label={t('sectionEditor.addSection.notesLabel')}
                size="small"
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isBusy}
                placeholder={t('sectionEditor.addSection.notesPlaceholder')}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSection}
                disabled={isBusy || !chatId || !startMessageId || !endMessageId}
                sx={{ whiteSpace: 'nowrap', minWidth: '100px' }}
              >
                {t('sectionEditor.addSection.addButton')}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Sections List */}
        <Typography variant="subtitle2" gutterBottom>
          {t('sectionEditor.currentSections.title', { count: sections.length })}
        </Typography>

        {sections.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {t('sectionEditor.currentSections.emptyState')}
          </Typography>
        ) : (
          <List>
            {sections.map((section) => (
              <ListItem key={section.id} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {formatMessageRange(section.start_message_id, section.end_message_id || 999999)}
                      </Typography>
                      <Chip
                        label={section.source}
                        size="small"
                        color={getSourceColor(section.source)}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      {section.notes && (
                        <Typography variant="body2" component="span">
                          {section.notes}
                          <br />
                        </Typography>
                      )}
                      <Typography variant="caption" component="span">
                        Chat {section.chat_id} • {t('sectionEditor.currentSections.added', { date: formatDate(section.created_at, 'PP') })}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => setEditingSection(section)}
                    disabled={isBusy}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => onDeleteSection(section.id)}
                    disabled={isBusy}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          {t('sectionEditor.actions.close')}
        </Button>
      </DialogActions>

      {/* Edit Section Dialog */}
      {editingSection && (
        <SectionEditDialog
          open={!!editingSection}
          section={editingSection}
          onClose={() => setEditingSection(null)}
          onUpdate={handleUpdateSection}
          isLoading={isBusy}
        />
      )}
    </Dialog>
  );
};

// ============================================================================
// Edit Section Dialog (TODO: Move to its own component file)
// ============================================================================

interface SectionEditDialogProps {
  open: boolean;
  section: LibrarySection;
  onClose: () => void;
  onUpdate: (sectionId: number, data: UpdateLibrarySectionRequest) => void;
  isLoading?: boolean;
}

const SectionEditDialog: React.FC<SectionEditDialogProps> = ({
  open,
  section,
  onClose,
  onUpdate,
  isLoading = false,
}) => {
  const { t } = useTranslation('components');
  const [startMessageId, setStartMessageId] = useState(String(section.start_message_id));
  const [endMessageId, setEndMessageId] = useState(String(section.end_message_id));
  const [notes, setNotes] = useState(section.notes || '');

  const handleSave = () => {
    const start = parseInt(startMessageId, 10);
    const end = parseInt(endMessageId, 10);

    if (Number.isNaN(start) || Number.isNaN(end)) {
      alert(t('sectionEditDialog.alertInvalidMessageIds'));
      return;
    }

    if (start >= end) {
      alert(t('sectionEditDialog.alertStartAfterEnd'));
      return;
    }

    onUpdate(section.id, {
      start_message_id: start,
      end_message_id: end,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('sectionEditDialog.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('sectionEditDialog.startMessageIdLabel')}
            type="number"
            fullWidth
            value={startMessageId}
            onChange={(e) => setStartMessageId(e.target.value)}
            disabled={isLoading}
          />
          <TextField
            label={t('sectionEditDialog.endMessageIdLabel')}
            type="number"
            fullWidth
            value={endMessageId}
            onChange={(e) => setEndMessageId(e.target.value)}
            disabled={isLoading}
          />
          <TextField
            label={t('sectionEditDialog.notesLabel')}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('sectionEditDialog.actions.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          {t('sectionEditDialog.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SectionEditor;
